// Draws a territory on a Google map: a thin line round every ZIP in it and one
// thick outline round the whole.
//
// The outline is the ZIP shapes joined into one, done here because the server
// only stores the shapes. Both kinds sit in one Data layer so they clear
// together, and neither takes clicks: the parcel layer under them still has to
// get every click on the map.

import polygonClipping from 'polygon-clipping';

const TERRITORY_BLUE = '#1570EF';
const ZIP_FILL_OPACITY = 0.1;
const ZIP_LINE_OPACITY = 0.7;
const ZIP_LINE_WEIGHT = 1;
const OUTLINE_WEIGHT = 4;

const KIND_ZIP = 'zip';
const KIND_OUTLINE = 'outline';

export default class TerritoryLayer {
    constructor(map) {
        this.map = map;
        this.zoomedIn = false;
        this.data = new google.maps.Data({ map, style: (feature) => this.styleFor(feature) });
        this.drawId = 0;
    }

    /** Zoomed in to the parcels the wash muddies their colours; the ZIP lines stay. */
    setZoomedIn(zoomedIn) {
        if (this.zoomedIn === zoomedIn) return;

        this.zoomedIn = zoomedIn;
        this.data.setStyle((feature) => this.styleFor(feature));
    }

    styleFor(feature) {
        if (feature.getProperty('kind') === KIND_OUTLINE) {
            return {
                fillOpacity: 0,
                strokeColor: TERRITORY_BLUE,
                strokeWeight: OUTLINE_WEIGHT,
                clickable: false,
                zIndex: 2,
            };
        }

        return {
            fillColor: TERRITORY_BLUE,
            fillOpacity: this.zoomedIn ? 0 : ZIP_FILL_OPACITY,
            strokeColor: TERRITORY_BLUE,
            strokeOpacity: ZIP_LINE_OPACITY,
            strokeWeight: ZIP_LINE_WEIGHT,
            clickable: false,
            zIndex: 1,
        };
    }

    /**
     * The line round each ZIP.
     *
     * @param {Array<{zip: string, geometry: object}>} boundaries
     * @returns {google.maps.LatLngBounds|null} the box all of them fit in
     */
    show(boundaries) {
        this.clear();

        this.data.addGeoJson({
            type: 'FeatureCollection',
            features: boundaries.map(({ zip, geometry }) => ({
                type: 'Feature',
                properties: { kind: KIND_ZIP, zip },
                geometry,
            })),
        });

        return boundsOf(boundaries);
    }

    /**
     * The thick outline round all the ZIPs drawn by show(). Waits a tick so the
     * ZIP lines are on screen first: joining a big territory takes a moment.
     *
     * Rejects when the shapes cannot be joined, so the caller says so rather
     * than leaving a territory with no outline and no word about why.
     */
    drawOutline(boundaries) {
        const drawId = ++this.drawId;

        if (boundaries.length === 0) return Promise.resolve();

        return new Promise((resolve, reject) => {
            setTimeout(() => {
                if (drawId !== this.drawId) return resolve();

                try {
                    const [first, ...rest] = boundaries.map(({ geometry }) => geometry.coordinates);

                    this.data.addGeoJson({
                        type: 'Feature',
                        properties: { kind: KIND_OUTLINE },
                        geometry: { type: 'MultiPolygon', coordinates: polygonClipping.union(first, ...rest) },
                    });
                    resolve();
                } catch (error) {
                    reject(error);
                }
            }, 0);
        });
    }

    clear() {
        this.drawId++;
        this.data.forEach((feature) => this.data.remove(feature));
    }

    destroy() {
        this.clear();
        this.data.setMap(null);
    }
}

function boundsOf(boundaries) {
    const bounds = new google.maps.LatLngBounds();
    let any = false;

    const extend = (coordinates) => {
        if (typeof coordinates[0] === 'number') {
            bounds.extend({ lng: coordinates[0], lat: coordinates[1] });
            any = true;
        } else {
            coordinates.forEach(extend);
        }
    };

    boundaries.forEach(({ geometry }) => extend(geometry.coordinates));

    return any ? bounds : null;
}
